import { components as c } from "@/components";
import useUsersOrganisationsRolesQuery from "@/features/auth/api/selectUsersOrganisationsRoles";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useAuth } from "@/utils/auth";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import type { OptionT } from "@patient-watch/forms";
import { templateSettingsSchema } from "@patient-watch/types";

const AdminForm = () => {
    const { templateId } = Route.useParams();
    const search = Route.useSearch();
    const auth = useAuth();
    const { markStepComplete, nextStep, setCurrentStep } = useTemplateNavigationStore();
    const { data: organisations } = useUsersOrganisationsRolesQuery(auth.id);
    const navigate = useNavigate();

    // Set this page as the current step
    useEffect(() => {
        setCurrentStep("organisation"); // Organisation is the first step (index 0)
    }, [setCurrentStep]);

    const form = useForm({
        defaultValues: {
            organisation_id: search.organisation_id || "",
        },
    });

    const handleNext = () => {
        const formData = form.getValues();
        if (formData.organisation_id) {
            const selectedOrg = organisations?.find((org) => org.organisation_id === formData.organisation_id);

            if (selectedOrg) {
                markStepComplete("organisation");
                nextStep();
                navigate({
                    to: "/templates/$templateId/trackers",
                    params: { templateId },
                    search: {
                        ...search,
                        organisation_id: selectedOrg.organisation_id ?? "",
                        organisation_name: selectedOrg.organisation_name ?? "",
                    },
                });
            }
        }
    };

    return (
        <c.ui.Form {...form}>
            <form className="w-full form">
                <c.fc.ComboBox
                    options={
                        (organisations?.map((org) => ({
                            label: org.organisation_name,
                            value: org.organisation_id,
                        })) ?? []) as OptionT[]
                    }
                    name="organisation_id"
                    label="Organisation"
                    placeholder="Select an organisation"
                />
                <TemplateStepFooter onPrimaryClick={handleNext} />
            </form>
        </c.ui.Form>
    );
};

// Use partial schema to allow any template settings fields
const organisationSearchSchema = templateSettingsSchema.partial();

export const Route = createFileRoute("/_app/templates/$templateId/organisation")({
    component: AdminForm,
    validateSearch: (search) => organisationSearchSchema.parse(search),
});
